// ===========================================
// P&L RATIO STRIP — the account's numbers, with reference bands
// ===========================================
// Five tiles above the CPG P&L: gross margin, deduction rate, delivery as a
// share of revenue, average days-to-pay, revenue per active account. The big
// number is always the account's own; the small line under it is a curated
// rule-of-thumb band (lib/pnlBenchmarks) with a calm position word — never a
// grade, never a benchmark dressed up as data. Missing inputs say what to
// enter and where instead of showing a bare dash.

import { fmtMoneyShort } from '../../utils/cpg';
import { PNL_BENCHMARKS, BENCHMARK_CAPTION, bandWording, type ReferenceBand } from '../../lib/pnlBenchmarks';
import type { PnlAnalysis } from '../../lib/pnlAnalysis';

const pct1 = (n: number) => `${(Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '')}%`;

function Tile({ title, value, position, context, reference, pointer }: {
  title: string;
  value: string | null;
  position?: string | null;   // "within the reference range" — words, from the band
  context?: string | null;    // the account's own context ("$549 across 12 drops")
  reference?: string | null;  // the curated band text
  pointer?: string | null;    // what to enter and where, when value is null
}) {
  return (
    <div className="card p-4 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">{title}</p>
      {value != null ? (
        <>
          <p className="text-2xl font-bold text-slate-900 tabular-nums mt-1">{value}</p>
          {position && <p className="text-xs text-slate-500 mt-0.5">{position}</p>}
          {context && <p className="text-[11px] text-slate-400 mt-0.5">{context}</p>}
        </>
      ) : (
        <>
          <p className="text-2xl font-bold text-slate-300 mt-1">—</p>
          {pointer && <p className="text-xs text-slate-500 mt-0.5">{pointer}</p>}
        </>
      )}
      {reference && <p className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">{reference}</p>}
    </div>
  );
}

const refLine = (b: ReferenceBand) => b.rangeText;

export default function RatioStrip({ a }: { a: PnlAnalysis }) {
  const B = PNL_BENCHMARKS;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Tile
          title="Gross margin"
          value={a.gmPct != null ? `${a.gmPct}%` : null}
          position={a.gmPct != null ? bandWording(a.gmPct, B.grossMargin) : null}
          context={a.gmPct != null ? 'deductions, COGS and delivery all in' : null}
          reference={refLine(B.grossMargin)}
          pointer="Enter cost per case on Wholesale → Channels and this fills in — nothing is estimated."
        />
        <Tile
          title="Deduction rate"
          value={a.dedRatePct != null ? pct1(a.dedRatePct) : null}
          position={a.dedRatePct != null ? bandWording(a.dedRatePct, B.deductionRate) : null}
          context={a.dedRatePct != null ? 'of revenue, on the deductions ledger' : null}
          reference={refLine(B.deductionRate)}
          pointer="Log distributor deductions on Wholesale → Deductions and the rate appears."
        />
        <Tile
          title="Delivery cost"
          value={a.deliveryPct != null ? pct1(a.deliveryPct) : null}
          position={a.deliveryPct != null ? bandWording(a.deliveryPct, B.deliveryPct) : null}
          context={a.deliveryCents > 0 ? `${fmtMoneyShort(a.deliveryCents)}${a.drops > 0 ? ` across ${a.drops} drops` : ''}` : null}
          reference={refLine(B.deliveryPct)}
          pointer="Record a delivery cost on each order and this reads as a share of revenue."
        />
        <Tile
          title="Avg days to pay"
          value={a.avgDaysToPay != null ? `${a.avgDaysToPay} days` : null}
          position={a.avgDaysToPay != null ? bandWording(a.avgDaysToPay, B.daysToPay) : null}
          context={a.avgDaysToPay != null
            ? `${a.paidInvoiceCount} paid invoice${a.paidInvoiceCount === 1 ? '' : 's'}${a.statedTermsDays != null ? ` · your stated terms: Net ${a.statedTermsDays}` : ''}`
            : null}
          reference={a.statedTermsDays != null ? `${refLine(B.daysToPay)} — yours: Net ${a.statedTermsDays}` : refLine(B.daysToPay)}
          pointer={a.openArLabel
            ? `Fills in as invoices are marked paid — ${a.openArLabel} is open now on Wholesale → Invoices.`
            : 'Fills in as invoices are issued and marked paid on Wholesale → Invoices.'}
        />
        <Tile
          title="Revenue / active account"
          value={a.revPerAccountCents != null ? fmtMoneyShort(a.revPerAccountCents) : null}
          context={a.activeAccounts != null && a.activeAccounts > 0 ? `${a.activeAccounts} account${a.activeAccounts === 1 ? '' : 's'} with recorded revenue` : null}
          reference="no industry band — depends on account mix; watch the trend"
          pointer="Appears once orders carry revenue — confirm the first wholesale order and it lands here."
        />
      </div>
      <p className="text-[11px] text-slate-400 mt-2">{BENCHMARK_CAPTION}</p>
    </div>
  );
}
